import { useNavigate, useParams } from 'react-router-dom';
import { Page } from 'components/Page/Page';
import { usePricingConfig } from 'services/repositories/pricingConfigs/pricingConfigs';
import { PricingConfigForm } from './PricingConfigForm/PricingConfigForm';
import { usePriceConfigsPartners } from 'hooks/usePriceConfigsPartners/usePriceConfigsPartners';
import { removeUploadedPriceConfig } from 'stores/priceConfigs/priceConfigs';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { createHandle, createLoader } from 'services/breadcrumbs/breadcrumbs';
import { PricingConfigRoutes } from 'support/types';
import { TFunction } from 'i18next';
import { RouteParams } from 'support/helpers/types/types';
import { EMPTY_FIELD } from 'support/helpers/const/const';

export function EditPricingConfigsPage() {
  const navigate = useNavigate();
  const { id: pricingConfigId, partnerId } = useParams();
  const { data: pricingConfig, isLoading: pricingConfigLoading } = usePricingConfig({
    variables: {
      pricingConfigId,
      partnerId,
    },
  });
  const { data: partnersWithInvoices, isLoading: connectedPartnersLoading } = usePriceConfigsPartners();

  const onCancelOrSuccess = () => {
    removeUploadedPriceConfig();
    navigate(-1);
  };

  const isPageLoading = partnerId ? connectedPartnersLoading || pricingConfigLoading : connectedPartnersLoading;
  return (
    <Page isLoading={isPageLoading}>
      <PricingConfigForm
        connectedPartners={partnersWithInvoices}
        pricingConfig={pricingConfig}
        onCancel={onCancelOrSuccess}
        onSuccess={onCancelOrSuccess}
      />
    </Page>
  );
}

type ResponseType = Awaited<ReturnType<typeof usePricingConfig.queryFn>>;
EditPricingConfigsPage.handle = createHandle<ResponseType, RouteParams<PricingConfigRoutes.view>>((params, data) => ({
  label: (t: TFunction) =>
    t('priceList:details.breadcrumb', {
      name: data?.name || EMPTY_FIELD,
    }),
  to: routeToPage(PricingConfigRoutes.view, params),
  key: PricingConfigRoutes.view,
}));

EditPricingConfigsPage.loader = createLoader(
  ({ params }) => usePricingConfig.getKey({ pricingConfigId: params.id, partnerId: params.partnerId }),
  usePricingConfig.queryFn,
);
